import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, ListChecks, FileText, Loader2, Copy, ExternalLink, Trash2, Edit3, PlayCircle, PauseCircle, X } from 'lucide-react';
import { toast } from 'sonner';
import { publicUrl } from '@/lib/publicUrl';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('masterToken') || localStorage.getItem('master_token') || localStorage.getItem('auth_token') || localStorage.getItem('token')}` });

const STATUS_BADGE = {
  draft:     { color: 'bg-zinc-500/15 text-zinc-300 border-zinc-500/20',   Icon: FileText,    label: 'Draft' },
  published: { color: 'bg-green-500/15 text-green-400 border-green-500/20', Icon: PlayCircle,  label: 'Published' },
  paused:    { color: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/20', Icon: PauseCircle, label: 'Paused' },
  closed:    { color: 'bg-red-500/15 text-red-400 border-red-500/20',     Icon: X,           label: 'Closed' },
};

export default function MasterForms() {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await fetch(`${API}/master/forms`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setForms(data.forms || []);
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const remove = async (form_id, title) => {
    if (!confirm(`Delete form "${title}"? This permanently removes the form and all its submissions.`)) return;
    const res = await fetch(`${API}/master/forms/${form_id}`, { method: 'DELETE', headers: authHeaders() });
    if (res.ok) { toast.success('Form deleted'); load(); }
    else toast.error('Could not delete form');
  };

  const copyLink = (slug) => {
    const url = publicUrl(`/forms/${slug}`);
    navigator.clipboard.writeText(url);
    toast.success('Public link copied');
  };

  const setStatus = async (f, status) => {
    const body = JSON.stringify({
      title: f.title, description: f.description || '', slug: f.slug,
      fields: f.fields || [], status,
      submit_button_label: f.submit_button_label || 'Submit',
      success_message: f.success_message || 'Thanks!',
      cover_image_url: f.cover_image_url || '',
      accent_color: f.accent_color || '#FACC15',
      send_email_to: f.send_email_to || '',
    });
    const res = await fetch(`${API}/master/forms/${f.form_id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeaders() }, body });
    if (res.ok) { toast.success(`Status: ${status}`); load(); }
    else toast.error('Could not update status');
  };

  return (
    <div className="space-y-5" data-testid="master-forms-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white font-syne">Forms</h1>
          <p className="text-[#9CA3AF] text-sm mt-1">Build forms (contact, surveys, signups, anything) — share by link or iframe embed anywhere on or off this site.</p>
        </div>
        <Link to="/master/forms/builder" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-yellow-400 hover:bg-yellow-300 text-black font-bold text-sm" data-testid="new-form-btn">
          <Plus size={16} /> New Form
        </Link>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-[#A78BFA]" /></div>
      ) : forms.length === 0 ? (
        <div className="text-center py-16 rounded-2xl border border-dashed border-white/10">
          <FileText size={32} className="text-[#A78BFA] mx-auto mb-3" />
          <p className="text-white font-semibold">No forms yet</p>
          <p className="text-[#9CA3AF] text-sm">Create your first form to start collecting responses.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {forms.map((f) => {
            const status = f.status || (f.is_published ? 'published' : 'draft');
            const sb = STATUS_BADGE[status] || STATUS_BADGE.draft;
            return (
              <div key={f.form_id} className="rounded-2xl bg-white/5 border border-white/10 p-5 hover:border-purple-500/40 transition" data-testid={`form-card-${f.form_id}`}>
                <div className="flex items-start justify-between mb-3 gap-2">
                  <h3 className="font-syne text-white font-bold truncate flex-1">{f.title}</h3>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${sb.color} flex items-center gap-1 flex-shrink-0`}><sb.Icon size={10} /> {sb.label}</span>
                </div>
                <div className="text-xs text-[#9CA3AF] mb-1 truncate">/forms/{f.slug}</div>
                <div className="flex items-center gap-3 text-xs text-[#9CA3AF] mb-4">
                  <span className="flex items-center gap-1"><FileText size={11} /> {f.fields?.length || 0} fields</span>
                  <span className="flex items-center gap-1"><ListChecks size={11} /> {f.total_submissions || 0} responses</span>
                </div>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <Link to={`/master/forms/builder/${f.form_id}`} className="px-3 py-1.5 rounded-lg bg-purple-500/15 hover:bg-purple-500/25 text-purple-300 text-xs font-semibold flex items-center justify-center gap-1" data-testid={`form-edit-${f.form_id}`}><Edit3 size={11} /> Edit</Link>
                  <Link to={`/master/forms/${f.form_id}/submissions`} className="px-3 py-1.5 rounded-lg bg-cyan-500/15 hover:bg-cyan-500/25 text-cyan-300 text-xs font-semibold flex items-center justify-center gap-1" data-testid={`form-subs-${f.form_id}`}><ListChecks size={11} /> Responses</Link>
                  <button onClick={() => copyLink(f.slug)} className="px-3 py-1.5 rounded-lg bg-yellow-500/15 hover:bg-yellow-500/25 text-yellow-300 text-xs font-semibold flex items-center justify-center gap-1" data-testid={`form-copy-${f.form_id}`}><Copy size={11} /> Copy Link</button>
                  <a href={`/forms/${f.slug}`} target="_blank" rel="noreferrer" className="px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white text-xs font-semibold flex items-center justify-center gap-1"><ExternalLink size={11} /> Open</a>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {status === 'published' ? (
                    <button onClick={() => setStatus(f, 'paused')} className="px-2 py-1.5 rounded-lg bg-yellow-500/10 hover:bg-yellow-500/20 text-yellow-300 text-[11px] font-semibold flex items-center justify-center gap-1" data-testid={`form-pause-${f.form_id}`}><PauseCircle size={10} /> Pause</button>
                  ) : (
                    <button onClick={() => setStatus(f, 'published')} className="px-2 py-1.5 rounded-lg bg-green-500/10 hover:bg-green-500/20 text-green-300 text-[11px] font-semibold flex items-center justify-center gap-1" data-testid={`form-publish-${f.form_id}`}><PlayCircle size={10} /> Publish</button>
                  )}
                  {status !== 'closed' ? (
                    <button onClick={() => setStatus(f, 'closed')} className="px-2 py-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-300 text-[11px] font-semibold flex items-center justify-center gap-1" data-testid={`form-close-${f.form_id}`}><X size={10} /> Close</button>
                  ) : (
                    <button onClick={() => setStatus(f, 'published')} className="px-2 py-1.5 rounded-lg bg-green-500/10 hover:bg-green-500/20 text-green-300 text-[11px] font-semibold flex items-center justify-center gap-1"><PlayCircle size={10} /> Reopen</button>
                  )}
                  <button onClick={() => remove(f.form_id, f.title)} className="px-2 py-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-300 text-[11px] font-semibold flex items-center justify-center gap-1" data-testid={`form-delete-${f.form_id}`}><Trash2 size={10} /> Delete</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
